import React, { useEffect } from "react";

import SupportEscalationScreen from "@/b2b/components/SupportEscalationScreen";
import config from "@/config";

/**
 * Shown when a tenant's bank check ran but landed in manual review.
 *
 * The sibling of the attempt-limit screen, and from the tenant's side the same situation: there
 * is no button that helps, and only ops finishing the review moves them on. So it gets the same
 * treatment — same layout, same automatic escalation, same honesty about whether ops actually
 * heard — via the shared SupportEscalationScreen.
 *
 * It replaces a purple gradient card that looked like it came from a different product, and a
 * "Call CirclePe Agent" button pointing at a support number we no longer answer.
 *
 * Copy follows the same rules as its sibling: short sentences, no idioms, and it says plainly
 * what is happening so a screenshot is enough for ops to know which queue this is.
 */
export default function BankingReviewScreen({ theme, partner }) {
  // The pre-existing ops EMAIL notification, kept exactly as it was.
  //
  // The Slack escalation below is strictly better — server-side dedupe, delivery confirmed back
  // to the screen, and it works from the magic-link id rather than a JWT this flow does not
  // always have. But ops may well have inbox rules and habits built on this email, and removing
  // a working notification because a better one now exists is how quiet gaps get created. Both
  // run; retiring this one is a deliberate decision for later, not a side effect of this change.
  //
  // ⚠️ Known limitation, unchanged here on purpose: the localStorage flag is global rather than
  // per-journey, so a tenant who reaches review on a second application from the same browser
  // never re-notifies. The Slack path does not share that flaw.
  useEffect(() => {
    const notifyBankingReview = async () => {
      try {
        const notificationKey = "banking-review-notified";
        if (localStorage.getItem(notificationKey)) return;

        const signupToken = localStorage.getItem("SIGNUP_TOKEN_CP");
        if (!signupToken) return;

        const tokenParts = signupToken.split(".");
        const decoded = JSON.parse(atob(tokenParts[1]));
        const userId = decoded?.userId;
        if (!userId) return;

        await fetch(`${config.API_URL}/bank/webhook/notify-banking-review`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${signupToken}`,
          },
          body: JSON.stringify({ user_id: userId }),
        });

        localStorage.setItem(notificationKey, "true");
      } catch (error) {
        console.error("Failed to send the banking review email", error);
      }
    };
    notifyBankingReview();
  }, []);

  return (
    <SupportEscalationScreen
      theme={theme}
      partnerName={partner}
      topic="banking-review"
      reason="Banking review pending"
      problemLine="My bank details are being checked and I am still waiting."
      copy={{
        idle: {
          title: "Please wait",
          body: "We are checking your details.",
        },
        sent: {
          title: "We are checking your bank details",
          body: "Our team is checking your bank details now. We will tell you when it is done. You do not need to do anything.",
        },
        failed: {
          title: "We are here to help",
          body: "Our team is checking your bank details. Please message us or call us if you need help.",
        },
      }}
    />
  );
}
